import React, { FC, useEffect, useState } from 'react';

// material-ui
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Pagination from '@mui/material/Pagination';
import Typography from '@mui/material/Typography';

// project imports
import List from './Usage';
import MainCard from 'ui-component/cards/MainCard';
import { gridSpacing } from 'store/constant';

// assets
import { IconSearch } from '@tabler/icons-react';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { getAgencyNameFromStorage } from 'Application/utils';
import { TransactionTypes } from 'Application/models';
import Loader from 'ui-component/Loader';
import { useCreditStore } from 'Application/store';

// ==============================|| ALLOCATION LIST ||============================== //

interface UsageListProps {
    agencyName: string;
    currency: string;
}

export const UsageList: FC<UsageListProps> = ({ agencyName, currency }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [page, setPage] = useState<number>(1); // Page state for pagination
    const [pageSize, setPageSize] = useState<number>(10); // Rows per page state
    const [searchTerm, setSearchTerm] = useState<string>(''); // Search term state

    const type = TransactionTypes.CREDIT;

    const { getCreditReports, getCreditReportsData, isLoading, isError, error } = useCreditStore((state) => ({
        getCreditReports: state.getCreditReports,
        getCreditReportsData: state.getCreditReportsData,
        isLoading: state.isLoading,
        isError: state.isError,
        error: state.error
    }));

    useEffect(() => {
        if (agencyName) {
            getCreditReports(agencyName, type, page, pageSize);
        }
    }, [agencyName, type, page, pageSize, getCreditReports]);

    useEffect(() => {
        if (agencyName && getCreditReportsData) {
            console.log('Fetched data for agency:', agencyName, getCreditReportsData);
        }
    }, [agencyName, getCreditReportsData]);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize);
        setPage(1); // Reset to page 1 when page size changes
        handleClose();
    };

    if (isLoading) {
        return <Loader />;
    }

    return (
        <MainCard
            title={
                <Grid container alignItems="center" justifyContent="space-between" spacing={gridSpacing}>
                    <Grid item>
                        <Typography variant="h3">Credit Usage Report</Typography>
                        <Typography pt={1} variant="subtitle2">
                            History of Credit Usage
                        </Typography>
                    </Grid>
                    <Grid item>
                        <OutlinedInput
                            id="input-search-list-style1"
                            placeholder="Search"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            startAdornment={
                                <InputAdornment position="start">
                                    <IconSearch stroke={1.5} size="16px" />
                                </InputAdornment>
                            }
                            size="small"
                        />
                    </Grid>
                </Grid>
            }
            content={false}
        >
            <List data={getCreditReportsData} currency={currency} />

            <Grid item xs={12} sx={{ p: 3 }}>
                <Grid container justifyContent="space-between" spacing={gridSpacing}>
                    <Grid item>
                        <Pagination
                            count={getCreditReportsData.totalPages} // You can update this to reflect the total pages based on API response
                            page={getCreditReportsData.currentPage}
                            onChange={handlePageChange}
                            color="primary"
                        />
                    </Grid>
                    <Grid item>
                        <Button
                            size="large"
                            sx={{ color: 'grey.900' }}
                            color="secondary"
                            endIcon={<ExpandMoreRoundedIcon />}
                            onClick={handleClick}
                        >
                            {pageSize} Rows
                        </Button>
                        {anchorEl && (
                            <Menu
                                id="menu-user-list-style1"
                                anchorEl={anchorEl}
                                keepMounted
                                open={Boolean(anchorEl)}
                                onClose={handleClose}
                                variant="selectedMenu"
                                anchorOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right'
                                }}
                                transformOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right'
                                }}
                            >
                                <MenuItem onClick={() => handlePageSizeChange(10)}>10 Rows</MenuItem>
                                <MenuItem onClick={() => handlePageSizeChange(20)}>20 Rows</MenuItem>
                                <MenuItem onClick={() => handlePageSizeChange(30)}>30 Rows</MenuItem>
                            </Menu>
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </MainCard>
    );
};

export default UsageList;
